import { useTranslation } from 'react-i18next';
import { MAIN_SITE_URL } from '@/mocks/categories';
import { GUIDE_IMAGES } from '@/mocks/guideImages';

export function WhatIsSection() {
  const { t } = useTranslation();
  return (
    <section id="spreadsheet" className="bg-background-100">
      <div className="mx-auto max-w-7xl px-4 py-14 md:px-6 md:py-20">
        <div className="mx-auto max-w-3xl text-center">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-accent-100 px-3 py-1 text-xs font-semibold text-accent-700">
            <i className="ri-information-line" />
            USFans Spreadsheet
          </span>
          <h2 className="mt-4 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
            {t('what_is.title')}
          </h2>
          <p className="mt-4 text-base leading-relaxed text-foreground-600">{t('what_is.body')}</p>
          <a
            href="#guides"
            className="mt-6 inline-flex items-center gap-2 rounded-full bg-foreground-900 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-foreground-800 whitespace-nowrap cursor-pointer"
          >
            {t('what_is.cta')}
            <i className="ri-arrow-right-line" />
          </a>
        </div>
      </div>
    </section>
  );
}

const FEATURE_ICONS: Record<string, string> = {
  spreadsheet: 'ri-file-list-3-line',
  review: 'ri-star-line',
  shipping: 'ri-truck-line',
  coupon: 'ri-coupon-line',
  warehouse: 'ri-archive-line',
  legit: 'ri-shield-check-line',
};

export function FeaturesSection() {
  const { t } = useTranslation();
  const features = ['spreadsheet', 'review', 'shipping', 'coupon', 'warehouse', 'legit'];

  return (
    <section id="features" className="mx-auto max-w-7xl px-4 py-14 md:px-6 md:py-20">
      <div className="mx-auto max-w-2xl text-center">
        <h2 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
          {t('features.title')}
        </h2>
        <p className="mt-3 text-sm text-foreground-600 md:text-base">{t('features.subtitle')}</p>
      </div>

      <div className="mt-10 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {features.map((key) => (
          <div
            key={key}
            className="flex flex-col gap-4 rounded-xl border border-background-200/70 bg-background-50 p-6 transition-colors hover:border-primary-200"
          >
            <span className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary-100 text-primary-600">
              <i className={`${FEATURE_ICONS[key]} text-2xl`} />
            </span>
            <div>
              <h3 className="font-heading text-base font-semibold text-foreground-900">
                {t(`features.${key}.title`)}
              </h3>
              <p className="mt-2 text-sm leading-relaxed text-foreground-600">
                {t(`features.${key}.desc`)}
              </p>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

export function GuidesSection() {
  const { t } = useTranslation();
  const cards = ['guides.card1', 'guides.card2', 'guides.card3', 'guides.card4', 'guides.card5', 'guides.card6'];

  return (
    <section id="guides" className="bg-background-100">
      <div className="mx-auto max-w-7xl px-4 py-14 md:px-6 md:py-20">
        <div className="mx-auto max-w-2xl text-center">
          <h2 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
            {t('guides.title')}
          </h2>
          <p className="mt-3 text-sm text-foreground-600 md:text-base">{t('guides.subtitle')}</p>
        </div>

        <div className="mt-10 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {cards.map((card, i) => (
            <a
              key={card}
              href="#guides"
              className="group flex flex-col overflow-hidden rounded-xl border border-background-200/70 bg-background-50 transition-colors hover:border-primary-200 cursor-pointer"
            >
              <div className="aspect-[4/3] w-full overflow-hidden bg-background-100">
                <img
                  src={GUIDE_IMAGES[i]}
                  alt={t(card)}
                  title={`${t(card)} — USFans Spreadsheet`}
                  className="h-full w-full object-cover object-top transition-transform duration-300 group-hover:scale-105"
                />
              </div>
              <div className="flex items-center justify-between gap-3 px-4 py-3.5">
                <span className="text-sm font-semibold text-foreground-800 group-hover:text-primary-700">
                  {t(card)}
                </span>
                <span className="flex h-6 w-6 shrink-0 items-center justify-center text-foreground-400 transition-transform group-hover:translate-x-0.5">
                  <i className="ri-arrow-right-line" />
                </span>
              </div>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}

const WHY_ICONS = ['ri-stack-line', 'ri-book-open-line', 'ri-table-line', 'ri-refresh-line', 'ri-shield-check-line', 'ri-global-line'];

export function WhySection() {
  const { t } = useTranslation();
  const items = [1, 2, 3, 4, 5, 6];

  return (
    <section className="mx-auto max-w-7xl px-4 py-14 md:px-6 md:py-20">
      <div className="mx-auto max-w-2xl text-center">
        <h2 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
          {t('why.title')}
        </h2>
        <p className="mt-3 text-sm text-foreground-600 md:text-base">{t('why.subtitle')}</p>
      </div>

      <div className="mt-10 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {items.map((n) => (
          <div key={n} className="flex items-start gap-4 rounded-xl border border-background-200/70 bg-background-50 p-5">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-accent-100 text-accent-700">
              <i className={`${WHY_ICONS[n - 1]} text-lg`} />
            </span>
            <div>
              <h3 className="font-heading text-sm font-semibold text-foreground-900">
                {t(`why.${n}.title`)}
              </h3>
              <p className="mt-1.5 text-sm leading-relaxed text-foreground-600">
                {t(`why.${n}.desc`)}
              </p>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

export function CompareSafetySection() {
  const { t } = useTranslation();
  return (
    <section className="bg-background-100">
      <div className="mx-auto grid max-w-7xl grid-cols-1 gap-6 px-4 py-14 md:px-6 md:py-20 lg:grid-cols-2">
        <div className="rounded-xl border border-background-200/70 bg-background-50 p-7">
          <span className="flex h-12 w-12 items-center justify-center rounded-lg bg-secondary-100 text-secondary-700">
            <i className="ri-scales-line text-2xl" />
          </span>
          <h2 className="mt-5 font-heading text-xl font-bold text-foreground-950 md:text-2xl">
            {t('compare.title')}
          </h2>
          <p className="mt-3 text-sm leading-relaxed text-foreground-600">{t('compare.body')}</p>
          <a
            href={MAIN_SITE_URL}
            target="_blank"
            rel="noopener nofollow"
            className="mt-5 inline-flex items-center gap-2 rounded-full bg-secondary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-secondary-600 whitespace-nowrap cursor-pointer"
          >
            {t('compare.cta')}
            <i className="ri-arrow-right-up-line" />
          </a>
        </div>

        <div id="safety" className="rounded-xl border border-accent-200 bg-accent-50 p-7">
          <span className="flex h-12 w-12 items-center justify-center rounded-lg bg-accent-100 text-accent-700">
            <i className="ri-shield-check-line text-2xl" />
          </span>
          <h2 className="mt-5 font-heading text-xl font-bold text-foreground-950 md:text-2xl">
            {t('safety.title')}
          </h2>
          <p className="mt-3 text-sm leading-relaxed text-foreground-600">{t('safety.body')}</p>
          <a
            href="#faq"
            className="mt-5 inline-flex items-center gap-2 rounded-full bg-accent-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-accent-600 whitespace-nowrap cursor-pointer"
          >
            {t('safety.cta')}
            <i className="ri-arrow-right-line" />
          </a>
        </div>
      </div>
    </section>
  );
}